// Feature Documentation: docs/features/external-case-table-enhancements.md
// Feature Documentation: docs/features/external-case-table-modern.md
// Feature Documentation: docs/features/external-case-file-view.md
import React, { useState, useEffect, useContext } from 'react';
import { callApi } from '../callApi';
import { AppContext } from '../components/AppContext';
import {
  Box, Grid, Flex, Text, Button, Link,
  Table, Thead, Tbody, Tr, Th, Td, VStack, Heading,
  Spinner, useToast, Select, Input, HStack, Badge,
  Modal, ModalOverlay, ModalContent, ModalHeader, 
  ModalFooter, ModalBody, ModalCloseButton, useDisclosure,
  List, ListItem, ListIcon, Checkbox, IconButton
} from "@chakra-ui/react";
import { AttachmentIcon, ChevronUpIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import HeaderMenus from './HeaderMenus';

function ExternalCaseTableModern() {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const toast = useToast();
  const [filters, setFilters] = useState({
    search_term: '',
    limit: '50',
    has_cpt: false,
    has_medical_necessity: false
  });
  
  const [sortConfig, setSortConfig] = useState({
    key: 'date_created',
    direction: 'desc'
  });

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });

  // Modal state
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedCase, setSelectedCase] = useState(null);
  const [caseDetails, setCaseDetails] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  const { accessToken } = useContext(AppContext);

  const bgColor = 'white';
  const borderColor = 'gray.200';
  const tableHeaderBg = 'gray.50';

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    // Trigger reload with new sort
    loadCaseData(filters, { key, direction }, pagination.page);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      loadCaseData(filters, sortConfig, newPage);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      loadCaseData(filters, sortConfig, 1); // Reset to page 1 on search
    }
  };

  const handleClearFilters = () => {
    const defaultFilters = {
      search_term: '',
      limit: '50',
      has_cpt: false,
      has_medical_necessity: false
    };
    setFilters(defaultFilters);
    setSortConfig({ key: 'date_created', direction: 'desc' });
    loadCaseData(defaultFilters, { key: 'date_created', direction: 'desc' }, 1);
  };

  const handleViewFiles = async (caseItem) => {
    setSelectedCase(caseItem);
    setCaseDetails(null);
    onOpen();
    setIsLoadingDetails(true);

    try {
      const response = await callApi(`/get_case_details/${caseItem.id}`, accessToken);
      if (!response.ok) {
        throw new Error('Failed to load case details');
      }
      const result = await response.json();
      setCaseDetails(result);
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to load file details",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const loadCaseData = async (currentFilters = filters, currentSort = sortConfig, page = 1) => {
    setIsLoading(true);
    setError(null);
    
    const params = new URLSearchParams();
    if (currentFilters.search_term) params.append('search_term', currentFilters.search_term);
    if (currentFilters.limit) params.append('limit', currentFilters.limit);
    if (currentFilters.has_cpt) params.append('has_cpt', 'true');
    if (currentFilters.has_medical_necessity) params.append('has_medical_necessity', 'true');
    
    params.append('page', page);
    params.append('sort_by', currentSort.key);
    params.append('sort_order', currentSort.direction);

    const queryString = params.toString();

    try {
      const response = await callApi(`/get_external_cases_modern?${queryString}`, accessToken);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to load case data');
      }
      
      const result = await response.json();
      
      if (result.status === 'success') {
        setData(result);
        setPagination({
          page: result.page,
          limit: result.limit,
          total: result.total_count,
          totalPages: result.total_pages
        });
        
        toast({
          title: "Data loaded",
          description: "Case data refreshed successfully",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      } else {
        throw new Error('Failed to load case data');
      }
    } catch (err) {
      const errorMessage = `Error loading case data: ${err.message}`;
      setError(errorMessage);
      
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCaseData();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString || dateString === 'None') return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'green';
      case 'in_progress': return 'blue';
      case 'failed': return 'red';
      case 'pending': return 'yellow';
      default: return 'gray';
    }
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === 'asc' ? <ChevronUpIcon ml={1} /> : <ChevronDownIcon ml={1} />;
  };

  const SortableHeader = ({ label, columnKey }) => (
    <Th 
      cursor="pointer" 
      onClick={() => handleSort(columnKey)}
      _hover={{ bg: 'gray.100' }}
    >
      <Flex align="center">
        {label}
        <SortIcon columnKey={columnKey} />
      </Flex>
    </Th>
  );

  return (
    <Box>
      <HeaderMenus />
      
      <Box bg={bgColor} p={6} minH="calc(100vh - 60px)">
        <Box mb={6} p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
          <Heading size="sm" mb={4}>Filters</Heading>
          <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={4} alignItems="end">
            <Box>
              <Text mb={2} fontSize="sm" fontWeight="medium">Search</Text>
              <Input 
                name="search_term" 
                value={filters.search_term} 
                onChange={handleFilterChange}
                onKeyDown={handleKeyDown}
                placeholder="Case ID, Description..."
              />
            </Box>
            
            <Box>
              <Text mb={2} fontSize="sm" fontWeight="medium">Results per page</Text>
              <Select name="limit" value={filters.limit} onChange={handleFilterChange}>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
              </Select>
            </Box>

            <Box>
               <Text mb={2} fontSize="sm" fontWeight="medium">Status Filters</Text>
               <HStack spacing={4}>
                 <Checkbox 
                   name="has_cpt" 
                   isChecked={filters.has_cpt} 
                   onChange={handleFilterChange}
                 >
                   Has CPT
                 </Checkbox>
                 <Checkbox 
                   name="has_medical_necessity" 
                   isChecked={filters.has_medical_necessity} 
                   onChange={handleFilterChange}
                 >
                   Has Med Nec
                 </Checkbox>
               </HStack>
            </Box>

            <HStack spacing={4}>
              <Button colorScheme="blue" onClick={() => loadCaseData(filters, sortConfig, 1)} isLoading={isLoading}>Apply Filters</Button>
              <Button variant="outline" onClick={handleClearFilters}>Clear</Button>
            </HStack>
          </Grid>
        </Box>

        {error && (
          <Box p={4} mb={4} borderRadius="md" borderWidth="1px" borderColor="red.300" bg="red.50">
            <Text color="red.600">{error}</Text>
          </Box>
        )}
        
        {isLoading && (
          <Flex justify="center" align="center" height="200px">
            <Spinner size="xl" color="blue.500" />
          </Flex>
        )}

        {!isLoading && data && (
          <Box>
            <Flex justifyContent="space-between" alignItems="center" mb={4}>
              <Heading size="md">External Case Results ({data.total_count} found)</Heading>
              <Button 
                colorScheme="blue" 
                size="sm" 
                onClick={() => loadCaseData(filters, sortConfig, pagination.page)}
                isLoading={isLoading}
              >
                Refresh Data
              </Button>
            </Flex>
            
            {data.cases.length > 0 ? (
              <Box>
                <Box overflowX="auto" borderRadius="md" borderWidth="1px" borderColor={borderColor} mb={4}>
                  <Table variant="simple" size="sm">
                    <Thead bg={tableHeaderBg}>
                      <Tr>
                        <SortableHeader label="Case ID" columnKey="external_case_id" />
                        <SortableHeader label="Patient Name" columnKey="patient_name" />
                        <SortableHeader label="Patient ID" columnKey="patient_id" />
                        <SortableHeader label="Description" columnKey="case_description" />
                        <SortableHeader label="Files" columnKey="file_count" />
                        <SortableHeader label="Type" columnKey="case_type" />
                        <SortableHeader label="Status" columnKey="status" />
                        <SortableHeader label="CPT" columnKey="cpt_count" />
                        <SortableHeader label="Med Nec" columnKey="med_nec_count" />
                        <SortableHeader label="Created" columnKey="date_created" />
                        <SortableHeader label="Updated" columnKey="date_updated" />
                      </Tr>
                    </Thead>
                    <Tbody>
                      {data.cases.map((caseItem) => (
                        <Tr key={caseItem.id}>
                          <Td
                            onClick={() => {
                              const url = `/full-cpt-coder?caseId=${caseItem.id}`;
                              window.open(url, "_blank");
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            <Text fontWeight="medium" color="blue.600">
                              {caseItem.external_case_id}
                            </Text>
                          </Td>
                          <Td>
                            <Text>{caseItem.patient_name || '-'}</Text>
                          </Td>
                          <Td>
                            <Text>{caseItem.patient_id || '-'}</Text>
                          </Td>
                          <Td>
                            <Text noOfLines={2} title={caseItem.case_description}>
                              {caseItem.case_description || 'N/A'}
                            </Text>
                          </Td>
                          <Td>
                            <Button 
                              size="xs" 
                              variant="outline" 
                              colorScheme="blue"
                              onClick={() => handleViewFiles(caseItem)}
                              leftIcon={<AttachmentIcon />}
                            >
                              {caseItem.file_count || 0}
                            </Button>
                          </Td>
                          <Td>
                            <Badge>{caseItem.case_type || 'Unknown'}</Badge>
                          </Td>
                          <Td>
                            <Badge colorScheme={getStatusColor(caseItem.status)}>
                              {caseItem.status || 'Pending'}
                            </Badge>
                          </Td>
                          <Td>
                            {caseItem.has_cpt ? (
                              <Badge colorScheme="green">Done</Badge>
                            ) : (
                              <Text fontSize="xs" color="gray.400">-</Text>
                            )}
                          </Td>
                          <Td>
                            {caseItem.has_medical_necessity ? (
                              <Link 
                                href={`/analyze-case-mn/${caseItem.external_case_id}?claim_type=${
                                  caseItem.med_nec_claim_types && caseItem.med_nec_claim_types.includes('pro') 
                                    ? 'pro' 
                                    : (caseItem.med_nec_claim_types && caseItem.med_nec_claim_types[0]) || 'pro'
                                }`}
                                isExternal
                                _hover={{ textDecoration: 'none' }}
                              >
                                <Text color="blue.600" fontWeight="medium" _hover={{ textDecoration: 'underline' }}>
                                  Open
                                </Text>
                              </Link>
                            ) : (
                              <Text fontSize="xs" color="gray.400">-</Text>
                            )}
                          </Td>
                          <Td>{formatDate(caseItem.date_created)}</Td>
                          <Td>{formatDate(caseItem.date_updated)}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>

                {/* Pagination Controls */}
                <Flex justify="space-between" align="center" p={2}>
                  <Text fontSize="sm" color="gray.600">
                    Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
                  </Text>
                  <HStack>
                    <IconButton
                      icon={<ChevronLeftIcon />}
                      onClick={() => handlePageChange(pagination.page - 1)}
                      isDisabled={pagination.page === 1}
                      aria-label="Previous Page"
                      size="sm"
                    />
                    <Text fontSize="sm">
                      Page {pagination.page} of {pagination.totalPages}
                    </Text>
                    <IconButton
                      icon={<ChevronRightIcon />}
                      onClick={() => handlePageChange(pagination.page + 1)}
                      isDisabled={pagination.page === pagination.totalPages}
                      aria-label="Next Page"
                      size="sm"
                    />
                  </HStack>
                </Flex>
              </Box>
            ) : (
              <Box p={4} textAlign="center" borderRadius="md" borderWidth="1px">
                <Text>No cases found matching your criteria.</Text>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* File Details Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Case Files: {selectedCase?.external_case_id}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {isLoadingDetails ? (
              <Flex justify="center" align="center" height="100px">
                <Spinner />
              </Flex>
            ) : caseDetails && caseDetails.files && caseDetails.files.length > 0 ? (
              <List spacing={3}>
                {caseDetails.files.map((file) => (
                  <ListItem key={file.id} p={2} borderWidth="1px" borderRadius="md">
                    <Flex justify="space-between" align="center">
                      <HStack>
                        <ListIcon as={AttachmentIcon} color="blue.500" />
                        <VStack align="start" spacing={0}>
                          <Text fontWeight="medium">{file.file_name}</Text>
                          <Text fontSize="xs" color="gray.500">
                            {file.file_type_description} • {(file.file_size / 1024).toFixed(1)} KB
                          </Text>
                        </VStack>
                      </HStack>
                      <Badge colorScheme="green">{file.document_category || 'General'}</Badge>
                    </Flex>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Text color="gray.500" textAlign="center">No files found for this case.</Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default ExternalCaseTableModern;